import type { ColumnDef } from '@tanstack/react-table'
import Status from '../components/UI/Status'
import type { ICurrentOrder, IOrder, ITotalOrderedInfo } from '../models/models'
import dateFormat from 'dateformat'
import SliderModal from '../components/UI/SliderModal'
import SuspendOrderButton from '../components/SuspendOrderButton'

export const ordersColumns: ColumnDef<IOrder>[] = [
	{
		accessorKey: 'id',
		header: 'Номер замовлення',
	},
	{
		accessorKey: 'status',
		header: 'Статус',
		cell: (value) => <Status type={value.getValue<any>()} />,
	},
	{
		accessorKey: 'userFio',
		header: 'ПІБ/Назва',
		minSize: 300,
	},
	{
		accessorKey: 'clientType',
		header: 'Клієнт',
		enableSorting: false,
	},
	{
		accessorKey: 'truckName',
		header: 'Паливовоз',
		enableSorting: false,
		cell: (value) => value.getValue<any>() || '-',
	},
	{
		accessorKey: 'truckerFio',
		header: 'ПІБ водія',
		minSize: 300,
		enableSorting: false,
		cell: (value) => value.getValue<any>() || '-',
	},
	{
		accessorKey: 'date',
		header: 'Дата',
		cell: (value) => dateFormat(value.row.original.date, 'dd.mm.yyyy'),
	},
	{
		accessorKey: 'date2',
		header: 'Час',
		cell: (value) => dateFormat(value.row.original.date, 'HH:MM'),
		enableSorting: false,
	},
	{
		accessorKey: 'endDate',
		header: 'Час закінчення',
		cell: (value) => dateFormat(value.row.original.endDate, 'dd.mm.yyyy HH:MM'),
		enableSorting: false,
	},
	{
		accessorKey: 'comment',
		header: 'Коментар',
		cell: (value) => value.row.original.comment || '-',
		minSize: 350,
		enableSorting: false,
	},
	{
		accessorKey: 'distanceKm',
		header: 'Дістанція/км',
		enableSorting: false,
	},
	{
		accessorKey: 'pickupMode',
		header: 'Самовивіз',
		cell: (value) => (value.getValue() ? 'Так' : 'Ні'),
		enableSorting: false,
	},
	{
		accessorKey: 'datecsUsed',
		header: 'Датекс',
		cell: (value) => (value.getValue() ? 'Так' : 'Ні'),
		enableSorting: false,
	},
	{
		accessorKey: 'fuelAmount',
		header: 'Замовлено дизелю',
	},
	{
		accessorKey: 'filledFuelAmount',
		header: 'Заправлено дизелю',
	},
	{
		accessorKey: 'adblueAmount',
		header: 'Замовлено AdBlue',
	},
	{
		accessorKey: 'filledAdblueAmount',
		header: 'Заправлено AdBlue',
	},
	{
		accessorKey: 'fuelPricePerLiter',
		header: 'Ціна дизелю за літр',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'adbluePricePerLiter',
		header: 'Ціна AdBlue за літр',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalFuelPricePerLiter',
		header: 'Сума дизелю',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalPrimeFuelPrice',
		header: 'Собівартість дизелю',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalFilledFuelPricePerLiter',
		header: 'Факт. сума за дизель',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalFilledAdbluePricePerLiter',
		header: 'Факт. сума за AdBlue',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'fuelProfit',
		header: 'Прибуток',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalAdbluePricePerLiter',
		header: 'Сума AdBlue',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'deliveryPrice',
		header: 'Ціна доставки',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalCost',
		header: 'Загальна сума',
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'region',
		header: 'Регіон',
		cell: (value) => value.row.original.region.name,
	},
]

export const orderInfoColumns: ColumnDef<ITotalOrderedInfo>[] = [
	{
		accessorKey: 'addressesCount',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				К-ть <br /> адрес
			</span>
		),
		enableSorting: false,
	},
	{
		accessorKey: 'dieselAmount',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Загальна к-ть <br /> Diesel
			</span>
		),
		enableSorting: false,
	},
	{
		accessorKey: 'adblueAmount',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Загальна к-ть <br /> AdBlue
			</span>
		),
		enableSorting: false,
	},
	{
		accessorKey: 'dieselPricePerLiter',
		header: () => <span style={{ textAlign: 'left' }}>Ціна за літр Diesel</span>,
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'adbluePricePerLiter',
		header: () => <span style={{ textAlign: 'left' }}>Ціна за літр AdBlue</span>,
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'dieselPrice',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Загальна сума <br /> за Diesel
			</span>
		),
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'adbluePrice',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Загальна сума <br /> за AdBlue
			</span>
		),
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'deliveryPrice',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Загальна сума <br /> за доставку
			</span>
		),
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'totalPrice',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Загальна <br /> сума
			</span>
		),
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
]

export const orderAddressesColumns: ColumnDef<ITotalOrderedInfo>[] = [
	{
		accessorKey: 'sequence',
		header: 'Точка',
		enableSorting: false,
	},
	{
		accessorKey: 'productName',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Назва <br /> продукту
			</span>
		),
		enableSorting: false,
	},
	{
		accessorKey: 'orderedAmount',
		header: 'Кількість',
		enableSorting: false,
	},
	{
		accessorKey: 'orderedPrice',
		header: 'Сума',
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'filledAmount',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Фактична <br /> кількість
			</span>
		),
		enableSorting: false,
	},
	{
		accessorKey: 'filledPrice',
		header: () => (
			<span style={{ textAlign: 'left' }}>
				Фактична <br /> сума
			</span>
		),
		enableSorting: false,
		cell: (value) => (Number(value.getValue<any>()) / 100).toFixed(2),
	},
	{
		accessorKey: 'receiptPhotos',
		header: 'Фото',
		enableSorting: false,
		cell: (value) => (value.getValue<any>()?.length ? <SliderModal files={value.getValue<any>()} /> : '-'),
	},
]

export const currentOrdersColumns: ColumnDef<ICurrentOrder>[] = [
	{
		accessorKey: 'truckName',
		header: 'Номер машини',
		enableSorting: false,
	},
	{
		accessorKey: 'trailerName',
		header: 'Номер причепу',
		enableSorting: false,
	},
	{
		accessorKey: 'id',
		header: 'Номер замовлення',
		enableSorting: false,
	},
	{
		accessorKey: 'preorder',
		header: 'Термінове',
		cell: (value) => value.row.original.preorder ? 'Ні' : 'Так',
		enableSorting: false,
	},
	{
		accessorKey: 'createdAt',
		header: 'Прийнято',
		cell: (value) => !!value.row.original?.createdAt 
			? dateFormat(value.row.original.createdAt, 'dd.mm.yyyy HH:MM') 
			: '-',
		enableSorting: false,
	},
	{
		accessorKey: 'customerStartedAt',
		header: 'Час замовлення',
		cell: (value) => !!value.row.original?.customerStartedAt 
			? dateFormat(value.row.original.customerStartedAt, 'dd.mm.yyyy HH:MM') 
			: '-',
		enableSorting: false,
	},
	{
		accessorKey: 'startedAt',
		header: 'Взято до роботи',
		cell: (value) => !!value.row.original?.startedAt 
			? dateFormat(value.row.original.startedAt, 'HH:MM') 
			: '-',
		enableSorting: false,
	},
	{
		accessorKey: 'fillStartedAt',
		header: 'Зливається',
		cell: (value) => !!value.row.original?.fillStartedAt 
			? dateFormat(value.row.original.fillStartedAt, 'HH:MM') 
			: '-',
		enableSorting: false,
	},
	{
		accessorKey: 'canBeSuspended',
		header: '',
		cell: (value) => !!value.row.original?.canBeSuspended 
			? (
				<div onClick={(ev) => ev.stopPropagation()}>
					<SuspendOrderButton id={value.row.original.id} />
				</div>
			)
			: '-',
		enableSorting: false,
	},
]